import {
    ArrowRightStartOnRectangleIcon,
    ChevronUpIcon,
    LightBulbIcon,
} from "@heroicons/react/16/solid";
import {
    BellIcon,
    ChartBarIcon,
    ChatBubbleLeftRightIcon,
    ChevronDoubleLeftIcon,
    ClipboardDocumentCheckIcon,
    Cog8ToothIcon,
    FolderIcon,
    HomeIcon,
    QuestionMarkCircleIcon,
    TableCellsIcon,
    UserGroupIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { ActionType } from "@sp-crm/core";
import { AddClientSidebar } from "components/clients/add-client-button/add-client-button";
import { CSVDownload } from "components/csv-download";
import { Avatar } from "components/layout/avatar";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "components/layout/dropdown";
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
} from "components/layout/sidebar";
import { NgRegionToggle } from "components/region-toggle/ng-region-toggle";
import { Badge } from "components/ui/badge";
import { getInitials } from "components/ui/persona";
import { Toggle } from "components/ui/toggle";
import { ReminderContext } from "contexts/reminder";
import { usePreviewDesignToggle } from "hooks/use-preview-design-toggle";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { hideMenu } from "store/actions/responsive";
import { logout } from "store/actions/session";
import { useProductBranding } from "store/selectors/branding";
import {
    useCurrentTenant,
    useCurrentUser,
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
    useIsMultiRegionUser,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { designSlice } from "store/slices/design";
import { ApplicationState } from "store/state";
import { allowedRegionsForAction, isAllowed } from "../../util/permissions";
import { AnnouncementsSidebarItem } from "./updates";
import { WhiteLabelRestricted } from "./white-label-restricted";

const NgAppLogo: React.FC = () => {
    const tenant = useCurrentTenant();

    const customTenantLogo = tenant?.hasLogo
        ? `/api/tenants/${tenant.tenantId}/logo`
        : null;

    if (customTenantLogo) {
        return (
            <Link
                to="/"
                className="mx-2 mb-4 rounded-lg shadow-sm block min-h-24 h-24 py-2 overflow-hidden lg:rounded-lg lg:bg-white lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10"
                style={{ lineHeight: 0, minHeight: "6rem" }}>
                <img
                    src={customTenantLogo}
                    className="object-contain w-full h-full"
                    alt="Tenant Logo"
                    style={{ display: "block", minHeight: "4rem" }}
                />
            </Link>
        );
    }

    return null;
};

type ExpandableSection = "clients" | "tasks" | "files" | "reports" | "settings";

const isClientsPath = (pathname: string): boolean => {
    return (
        pathname === "/" ||
        pathname === "/pipeline" ||
        pathname === "/timeline" ||
        pathname === "/transfers" ||
        pathname.startsWith("/clients")
    );
};

export const NgSidebarHamburgler: React.FC = () => {
    const dispatch = useDispatch();
    const user = useCurrentUser();
    const { pathname } = useLocation();
    const { settings } = useTenantSettings();
    const publicProfileSettingEnabled = settings.enablePublicProfile;
    const [csvExportPanelOpen, setCsvExportPanelOpen] = useState(false);
    const { branding } = useProductBranding();
    const sidebarBodyRef = useRef<HTMLDivElement>(null);

    const [expandedSections, setExpandedSections] = useState<ExpandableSection[]>(() => {
        const initialExpanded: ExpandableSection[] = [];
        if (isClientsPath(pathname)) {
            initialExpanded.push("clients");
        }
        if (pathname.startsWith("/files")) {
            initialExpanded.push("files");
        }
        if (pathname.startsWith("/tasks")) {
            initialExpanded.push("tasks");
        }
        if (pathname.startsWith("/reports")) {
            initialExpanded.push("reports");
        }
        if (pathname.startsWith("/settings")) {
            initialExpanded.push("settings");
        }
        return initialExpanded;
    });

    const toggleSection = useCallback((section: ExpandableSection) => {
        setExpandedSections(prev => {
            const collapsing = prev.includes(section);
            if (collapsing) {
                return prev.filter(s => s !== section);
            } else {
                if (section === "settings" && sidebarBodyRef.current) {
                    setTimeout(() => {
                        if (sidebarBodyRef.current) {
                            sidebarBodyRef.current.scrollTo({
                                top: sidebarBodyRef.current.scrollHeight,
                                behavior: "smooth",
                            });
                        }
                    }, 0);
                }
                return [...prev, section];
            }
        });
    }, []);

    const toggleClients = useCallback(() => toggleSection("clients"), [toggleSection]);
    const toggleTasks = useCallback(() => toggleSection("tasks"), [toggleSection]);
    const toggleFiles = useCallback(() => toggleSection("files"), [toggleSection]);
    const toggleReports = useCallback(() => toggleSection("reports"), [toggleSection]);
    const toggleSettings = useCallback(() => toggleSection("settings"), [toggleSection]);

    const reportsMode = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReports, state.permissions, state.region)
            ? "full-access"
            : "custom-only",
    );

    const showReferrals = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReference, state.permissions, state.region),
    );
    const smsEnabled = useFeature("sms");

    const isClientsActive = expandedSections.includes("clients");
    const isFilesActive = expandedSections.includes("files");
    const isTasksActive = expandedSections.includes("tasks");
    const isReportsActive = expandedSections.includes("reports");
    const isSettingsActive = expandedSections.includes("settings");
    const entitiesWithoutActiveTasksEnabled = useFeature("entitiesWithoutActiveTasks");
    const legacyReportsEnabled = useIsAllowed(ActionType.ViewReports);
    const viewAllEnabled = useIsAllowed(ActionType.ViewAllInvoices);
    const viewOwnEnabled = useIsAllowed(ActionType.ViewOwnInvoices);
    const viewCustomReportsEnabled = useIsAllowed(
        ActionType.AdvancedSearchExplicitExecute,
    );
    const csvDownloadEnabled = useIsAllowed(ActionType.ExportCsv);

    const isMultiRegionUser = useIsMultiRegionUser();
    const editAgencySettingsAllowed = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const editRegionSettingsAllowed = useIsAllowed(ActionType.UpdateRegionSettings);
    const manageUsersAllowed = useIsAllowed(ActionType.SelfManageModifyUser);
    const manageIntegrationsAllowed = useIsAllowedInWholeTenant(
        ActionType.ManageIntegrations,
    );
    const manageWorkflowsAllowed = useIsAllowed(ActionType.CreateWorkflow);

    const showAgencySettings = editAgencySettingsAllowed;
    const showRegionSettings =
        (editRegionSettingsAllowed && isMultiRegionUser) ||
        (editRegionSettingsAllowed && !editAgencySettingsAllowed);

    const regionState = useSelector((state: ApplicationState) => state.region);
    const region = regionState.regions.find(r => r.key === regionState.selectedRegion);
    const canAccessMultipleRegions = useSelector(
        (state: ApplicationState) =>
            allowedRegionsForAction(ActionType.ExportCsv, state.permissions).length > 1,
    );

    const showCsvInterface = useCallback(() => {
        setCsvExportPanelOpen(true);
    }, []);

    const { isReminderEnabled, setVisible, reminders } = useContext(ReminderContext);
    const remindersCount = reminders?.length || 0;
    const handleReminderClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setVisible(true);
            dispatch(hideMenu());
        },
        [setVisible, dispatch],
    );

    const { isUseNewDesignChecked, handlePreviewChange } = usePreviewDesignToggle();

    const handleLogout = useCallback(() => {
        logout(dispatch);
    }, [dispatch]);

    const handleCollapse = useCallback(() => {
        dispatch(designSlice.actions.setCompactSidebarState("collapsed"));
    }, [dispatch]);

    return (
        <Sidebar>
            <div className="flex items-center justify-between p-2 border-b border-zinc-200 dark:border-zinc-800">
                <button
                    onClick={handleCollapse}
                    className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                    title="Collapse sidebar">
                    <ChevronDoubleLeftIcon className="w-4 h-4" />
                </button>
            </div>
            <SidebarBody ref={sidebarBodyRef}>
                <NgAppLogo />
                <SidebarSection>
                    <NgRegionToggle />
                    {isReminderEnabled ? (
                        <SidebarItem current={false} onClick={handleReminderClick}>
                            <BellIcon />
                            <SidebarLabel className="flex-1">
                                <div className="flex justify-between items-center w-full">
                                    <div>Reminders</div>
                                    {remindersCount > 0 ? (
                                        <Badge
                                            value={remindersCount.toString()}
                                            type="error"
                                        />
                                    ) : null}
                                </div>
                            </SidebarLabel>
                        </SidebarItem>
                    ) : null}

                    <AddClientSidebar />
                    <SidebarItem
                        href="/pipeline"
                        expandable
                        expanded={isClientsActive}
                        onExpandToggle={toggleClients}
                        current={pathname.startsWith("/clients/show")}>
                        <UsersIcon />
                        <SidebarLabel>Clients</SidebarLabel>
                    </SidebarItem>
                    {isClientsActive ? (
                        <>
                            <SidebarItem
                                href="/pipeline"
                                current={pathname === "/pipeline" || pathname === "/"}>
                                <SidebarLabel className="ml-8">Pipeline</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/timeline"
                                current={pathname === "/timeline"}>
                                <SidebarLabel className="ml-8">Timeline</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/clients"
                                current={pathname === "/clients"}>
                                <SidebarLabel className="ml-8">All Clients</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/transfers"
                                current={pathname === "/transfers"}>
                                <SidebarLabel className="ml-8">Transfers</SidebarLabel>
                            </SidebarItem>
                        </>
                    ) : null}

                    <SidebarItem
                        href="/communities"
                        current={pathname.startsWith("/communities")}>
                        <HomeIcon />
                        <SidebarLabel>Communities</SidebarLabel>
                    </SidebarItem>

                    {showReferrals && (
                        <SidebarItem
                            href="/references"
                            current={pathname.startsWith("/references")}>
                            <UserIcon />
                            <SidebarLabel>Referrals</SidebarLabel>
                        </SidebarItem>
                    )}

                    <SidebarItem
                        href="/tasks"
                        expandable
                        expanded={isTasksActive}
                        onExpandToggle={toggleTasks}>
                        <ClipboardDocumentCheckIcon />
                        <SidebarLabel>Tasks</SidebarLabel>
                    </SidebarItem>
                    {isTasksActive ? (
                        <>
                            <SidebarItem href="/tasks" current={pathname === "/tasks"}>
                                <SidebarLabel className="ml-8">Active Tasks</SidebarLabel>
                            </SidebarItem>
                            {entitiesWithoutActiveTasksEnabled && (
                                <SidebarItem
                                    href="/tasks/missing"
                                    current={pathname === "/tasks/missing"}>
                                    <SidebarLabel className="ml-8">
                                        Entities Without Tasks
                                    </SidebarLabel>
                                </SidebarItem>
                            )}
                        </>
                    ) : null}

                    <SidebarItem
                        href="/files"
                        expandable
                        expanded={isFilesActive}
                        onExpandToggle={toggleFiles}>
                        <FolderIcon />
                        <SidebarLabel>Files</SidebarLabel>
                    </SidebarItem>
                    {isFilesActive ? (
                        <>
                            <SidebarItem href="/files" current={pathname === "/files"}>
                                <SidebarLabel className="ml-8">Our Files</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/files/signwise"
                                current={pathname.startsWith("/files/signwise")}>
                                <SidebarLabel className="ml-8">Signwise</SidebarLabel>
                            </SidebarItem>
                        </>
                    ) : null}

                    <SidebarItem
                        href={
                            reportsMode === "custom-only"
                                ? "/reports/show/custom"
                                : "/reports"
                        }
                        expandable
                        expanded={isReportsActive}
                        onExpandToggle={toggleReports}>
                        <ChartBarIcon />
                        <SidebarLabel>Reports</SidebarLabel>
                    </SidebarItem>
                    {isReportsActive ? (
                        <>
                            <SidebarItem
                                href="/reports"
                                current={pathname === "/reports"}>
                                <SidebarLabel className="ml-8">Overview</SidebarLabel>
                            </SidebarItem>
                            {legacyReportsEnabled &&
                            (viewAllEnabled || viewOwnEnabled) ? (
                                <SidebarItem
                                    href="/reports/show/revenue"
                                    current={pathname.startsWith(
                                        "/reports/show/revenue",
                                    )}>
                                    <SidebarLabel className="ml-8">
                                        Client Placement
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {legacyReportsEnabled ? (
                                <SidebarItem
                                    href="/reports/show/referrals"
                                    current={pathname.startsWith(
                                        "/reports/show/referrals",
                                    )}>
                                    <SidebarLabel className="ml-8">
                                        Referrals
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {viewCustomReportsEnabled ? (
                                <SidebarItem
                                    href="/reports/show/custom"
                                    current={pathname.startsWith("/reports/show/custom")}>
                                    <SidebarLabel className="ml-8">
                                        Custom Reports
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                        </>
                    ) : null}
                    {smsEnabled && (
                        <SidebarItem
                            href="/messaging"
                            current={pathname.startsWith("/messaging")}>
                            <ChatBubbleLeftRightIcon />
                            <SidebarLabel>Messaging</SidebarLabel>
                        </SidebarItem>
                    )}
                </SidebarSection>

                <SidebarSpacer />
                <SidebarSection>
                    <WhiteLabelRestricted>
                        <SidebarItem href="https://docs.seniorplace.io/" target="_blank">
                            <QuestionMarkCircleIcon />
                            <SidebarLabel>Tutorials</SidebarLabel>
                        </SidebarItem>
                        <AnnouncementsSidebarItem />
                    </WhiteLabelRestricted>
                    {publicProfileSettingEnabled ? (
                        <SidebarItem
                            href="/advisor-directory"
                            current={pathname.startsWith("/advisor-directory")}>
                            <UserGroupIcon />
                            <SidebarLabel>Advisor Directory</SidebarLabel>
                        </SidebarItem>
                    ) : null}
                    <SidebarItem
                        href="/settings"
                        expandable
                        expanded={isSettingsActive}
                        onExpandToggle={toggleSettings}>
                        <Cog8ToothIcon />
                        <SidebarLabel>Settings</SidebarLabel>
                    </SidebarItem>
                    {isSettingsActive ? (
                        <>
                            <SidebarItem
                                href="/settings/account"
                                current={pathname.startsWith("/settings/account")}>
                                <SidebarLabel className="ml-6">My settings</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/settings/templates"
                                current={pathname.startsWith("/settings/templates")}>
                                <SidebarLabel className="ml-6">
                                    Email Templates & Signatures
                                </SidebarLabel>
                            </SidebarItem>
                            {showAgencySettings ? (
                                <SidebarItem
                                    href="/settings/agency"
                                    current={pathname.startsWith("/settings/agency")}>
                                    <SidebarLabel className="ml-6">
                                        Agency settings
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {showRegionSettings ? (
                                <SidebarItem
                                    href="/settings/region"
                                    current={pathname.startsWith("/settings/region")}>
                                    <SidebarLabel className="ml-6">
                                        Office settings
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {manageWorkflowsAllowed ? (
                                <SidebarItem
                                    href="/settings/workflow"
                                    current={pathname.startsWith("/settings/workflow")}>
                                    <SidebarLabel className="ml-6">
                                        Workflows
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {manageUsersAllowed ? (
                                <SidebarItem
                                    href="/settings/users"
                                    current={pathname.startsWith("/settings/users")}>
                                    <SidebarLabel className="ml-6">
                                        User accounts
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {manageIntegrationsAllowed ? (
                                <SidebarItem
                                    href="/settings/integrations"
                                    current={pathname.startsWith(
                                        "/settings/integrations",
                                    )}>
                                    <SidebarLabel className="ml-6">
                                        Integrations
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                        </>
                    ) : null}
                    {user ? (
                        <SidebarItem
                            href="#"
                            className="lg:hidden"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                handleLogout();
                            }}>
                            <ArrowRightStartOnRectangleIcon />
                            <SidebarLabel>Sign out</SidebarLabel>
                        </SidebarItem>
                    ) : null}
                    <div className="px-3 py-2">
                        <Toggle
                            onChange={handlePreviewChange}
                            checked={isUseNewDesignChecked}
                            label="Use new design"
                        />
                    </div>
                </SidebarSection>
            </SidebarBody>

            {user ? (
                <SidebarFooter className="max-lg:hidden">
                    <Dropdown>
                        <DropdownButton as={SidebarItem}>
                            <span className="flex min-w-0 items-center gap-3">
                                <Avatar
                                    initials={getInitials(user.name)}
                                    className="size-10"
                                    square
                                    alt=""
                                />
                                <span className="min-w-0">
                                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                                        {user.name}
                                    </span>
                                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                                        {user.email}
                                    </span>
                                </span>
                            </span>
                            <ChevronUpIcon />
                        </DropdownButton>
                        <DropdownMenu className="min-w-64" anchor={"top start"}>
                            {csvDownloadEnabled ? (
                                <>
                                    <DropdownItem
                                        href="#"
                                        onClick={(e: React.MouseEvent) => {
                                            e.preventDefault();
                                            showCsvInterface();
                                        }}>
                                        <TableCellsIcon />
                                        <DropdownLabel>CSV Export</DropdownLabel>
                                    </DropdownItem>
                                    <DropdownDivider />
                                </>
                            ) : null}
                            <DropdownItem
                                onClick={() => {
                                    if (branding?.supportEmailAddress) {
                                        window.open(
                                            `mailto:${branding.supportEmailAddress}`,
                                            "_blank",
                                        );
                                    }
                                }}>
                                <LightBulbIcon />
                                <DropdownLabel>Share feedback</DropdownLabel>
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem
                                href="#"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    handleLogout();
                                }}>
                                <ArrowRightStartOnRectangleIcon />
                                <DropdownLabel>Sign out</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </SidebarFooter>
            ) : null}
            {csvExportPanelOpen && (
                <CSVDownload
                    regionName={region?.name || ""}
                    regionId={region?.id}
                    canAccessMultipleRegions={canAccessMultipleRegions}
                    onDismiss={() => setCsvExportPanelOpen(false)}
                />
            )}
        </Sidebar>
    );
};
